import { useMemo, useState } from 'react';
import { documentalApi } from '../../api/documental';

const STEPS = [
	'Subir Excel',
	'Revisar datos base',
	'Ingresar cambios',
	'Generar documentos',
];

const DEFAULT_CAMBIOS = {
	TELEFONO_DE_LA_EMPRESA: '',
	CORREO_EMPRESARIAL: '',
	CALLEDEL_NUEVO_DOMICILIO: '',
	SECTORDEL_NUEVO_DOMICILIO: '',
	CIUDADDEL_NUEVO_DOMICILIO: '',
	PROVINCIADEL_NUEVO_DOMICILIO: '',
	ACTIVIDAD_COMERCIAL: '',
	FECHA_ACTA: '',
	CIUDAD_ACTA: '',
	HORA_ACTA: '',
	orden_dia: [],
	socios_modificados: [],
};

export default function NuevoExpediente({ tipoTramite, empresaId, onClose, onCreated }) {
	const [step, setStep] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [excelData, setExcelData] = useState(null);
	const [datosBase, setDatosBase] = useState({
		empresa_nombre: '',
		empresa_rnc: '',
		socios: [],
	});
	const [datosCambios, setDatosCambios] = useState(DEFAULT_CAMBIOS);
	const [selectedPlantillas, setSelectedPlantillas] = useState([]);
	const [resultado, setResultado] = useState(null);

	const plantillas = useMemo(() => tipoTramite?.plantillas || [], [tipoTramite]);
	const schema = useMemo(() => tipoTramite?.schema_campos || {}, [tipoTramite]);
	const ordenDiaCatalogo = useMemo(
		() => (Array.isArray(schema?.orden_dia_catalogo) ? schema.orden_dia_catalogo : []),
		[schema]
	);

	const canContinue = useMemo(() => {
		if (step === 0) return !!excelData;
		if (step === 1) return !!datosBase.empresa_nombre;
		if (step === 2) return true;
		if (step === 3) return selectedPlantillas.length > 0;
		return false;
	}, [step, excelData, datosBase, selectedPlantillas]);

	const handleUploadExcel = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;
		try {
			setLoading(true);
			setError('');
			const res = await documentalApi.leerExcel(file);
			setExcelData(res);
			setDatosBase({
				empresa_nombre: res?.empresa?.nombre_comercial || '',
				empresa_rnc: res?.empresa?.rnc || '',
				socios: Array.isArray(res?.socios) ? res.socios : [],
			});
			const sociosIniciales = (Array.isArray(res?.socios) ? res.socios : []).map((s) => ({
				...s,
				firma_nombre: [s.nombre, s.apellido].filter(Boolean).join(' ').trim(),
				firma_cargo: s.cargo || '',
				direccion: [s.calle, s.no_casa, s.sector, s.ciudad, s.provincia].filter(Boolean).join(', '),
			}));
			setDatosCambios((prev) => ({
				...prev,
				TELEFONO_DE_LA_EMPRESA: res?.empresa?.telefono || '',
				CORREO_EMPRESARIAL: res?.empresa?.correo || '',
				socios_modificados: sociosIniciales,
				orden_dia: (ordenDiaCatalogo.length ? ordenDiaCatalogo : []).map((t) => ({ texto: t, activo: true })),
			}));
		} catch (e) {
			setError(e?.response?.data?.detail || e.message || 'No se pudo leer el Excel');
		} finally {
			setLoading(false);
		}
	};

	const handleGenerar = async () => {
		try {
			setLoading(true);
			setError('');
			const expediente = await documentalApi.createExpediente({
				tipo_tramite: tipoTramite.id,
				titulo: `${tipoTramite.nombre} - ${datosBase.empresa_nombre || 'Empresa'}`,
				empresa_nombre: datosBase.empresa_nombre,
				empresa_rnc: datosBase.empresa_rnc,
				...(empresaId ? { empresa: empresaId } : {}),
				datos_base: {
					empresa: excelData?.empresa || {},
					socios: datosBase.socios || [],
				},
				datos_cambios: datosCambios,
			});
			const gen = await documentalApi.generarDocumentos(expediente.id, selectedPlantillas);
			setResultado({ expediente, ...gen });
			if (onCreated) onCreated();
		} catch (e) {
			setError(e?.response?.data?.detail || e.message || 'No se pudieron generar los documentos');
		} finally {
			setLoading(false);
		}
	};

	const togglePlantilla = (slug) => {
		setSelectedPlantillas((prev) =>
			prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
		);
	};

	const updateSocio = (idx, field, value) => {
		setDatosCambios((prev) => {
			const socios = [...(prev.socios_modificados || [])];
			socios[idx] = { ...(socios[idx] || {}), [field]: value };
			return { ...prev, socios_modificados: socios };
		});
	};

	const updateOrdenDia = (idx, patch) => {
		setDatosCambios((prev) => {
			const list = [...(prev.orden_dia || [])];
			list[idx] = { ...(list[idx] || {}), ...patch };
			return { ...prev, orden_dia: list };
		});
	};

	const addOrdenDia = () => {
		setDatosCambios((prev) => ({
			...prev,
			orden_dia: [...(prev.orden_dia || []), { texto: '', activo: true }],
		}));
	};

	const removeOrdenDia = (idx) => {
		setDatosCambios((prev) => ({
			...prev,
			orden_dia: (prev.orden_dia || []).filter((_, i) => i !== idx),
		}));
	};

	return (
		<div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
			<div className="w-full max-w-5xl bg-white rounded-xl shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
				<div className="p-5 border-b border-gray-200">
					<h2 className="text-xl font-semibold text-gray-900">{tipoTramite?.nombre}</h2>
					<p className="text-sm text-gray-600 mt-1">Nuevo expediente documental</p>
					<div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
						{STEPS.map((label, idx) => (
							<div key={label} className={`text-xs rounded-lg px-2 py-1.5 text-center ${idx === step ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'}`}>
								{idx + 1}. {label}
							</div>
						))}
					</div>
				</div>

				<div className="p-5 space-y-4">
					{step === 0 && (
						<div className="space-y-3">
							<p className="text-sm text-gray-700">Sube el Excel con los datos actuales de la empresa.</p>
							<input type="file" accept=".xlsx,.xls" onChange={handleUploadExcel} />
							{excelData && (
								<div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg p-3">
									Excel cargado. Filas detectadas: {excelData.rows_detectadas || 0}.
								</div>
							)}
						</div>
					)}

					{step === 1 && (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
							<input className="border rounded-lg px-3 py-2" placeholder="Nombre empresa" value={datosBase.empresa_nombre} onChange={(e) => setDatosBase((p) => ({ ...p, empresa_nombre: e.target.value }))} />
							<input className="border rounded-lg px-3 py-2" placeholder="RNC" value={datosBase.empresa_rnc} onChange={(e) => setDatosBase((p) => ({ ...p, empresa_rnc: e.target.value }))} />
							<div className="md:col-span-2 border rounded-lg p-3 bg-gray-50">
								<p className="text-sm font-semibold text-gray-700 mb-2">Socios detectados</p>
								{(datosBase.socios || []).length === 0 ? (
									<p className="text-sm text-gray-500">No hay socios detectados en el Excel.</p>
								) : (
									<div className="space-y-1">
										{datosBase.socios.map((s) => (
											<div key={s.indice} className="text-sm text-gray-700">
												{s.nombre} {s.apellido} - {s.no_documento || 'Sin documento'} - {s.participacion || 'N/D'} cuotas
											</div>
										))}
									</div>
								)}
							</div>
						</div>
					)}

					{step === 2 && (
						<div className="space-y-5">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								{Object.keys(DEFAULT_CAMBIOS)
									.filter((key) => !['orden_dia', 'socios_modificados'].includes(key))
									.map((key) => (
										<input
											key={key}
											className="border rounded-lg px-3 py-2"
											placeholder={key}
											value={datosCambios[key] || ''}
											onChange={(e) => setDatosCambios((p) => ({ ...p, [key]: e.target.value }))}
										/>
									))}
							</div>

							<div className="border rounded-lg p-3">
								<div className="flex items-center justify-between mb-2">
									<p className="text-sm font-semibold text-gray-700">Socios / Firmas / Cuotas</p>
								</div>
								<div className="space-y-3">
									{(datosCambios.socios_modificados || []).map((s, idx) => (
										<div key={idx} className="grid grid-cols-1 md:grid-cols-3 gap-2 border rounded p-2">
											<input className="border rounded px-2 py-1 text-sm" placeholder="Nombre" value={s.nombre || ''} onChange={(e) => updateSocio(idx, 'nombre', e.target.value)} />
											<input className="border rounded px-2 py-1 text-sm" placeholder="Apellido" value={s.apellido || ''} onChange={(e) => updateSocio(idx, 'apellido', e.target.value)} />
											<input className="border rounded px-2 py-1 text-sm" placeholder="Documento" value={s.no_documento || ''} onChange={(e) => updateSocio(idx, 'no_documento', e.target.value)} />
											<input className="border rounded px-2 py-1 text-sm md:col-span-2" placeholder="Direccion" value={s.direccion || ''} onChange={(e) => updateSocio(idx, 'direccion', e.target.value)} />
											<input className="border rounded px-2 py-1 text-sm" placeholder="Cuotas" value={s.participacion || ''} onChange={(e) => updateSocio(idx, 'participacion', e.target.value)} />
											<input className="border rounded px-2 py-1 text-sm" placeholder="Cargo" value={s.cargo || ''} onChange={(e) => updateSocio(idx, 'cargo', e.target.value)} />
											<input className="border rounded px-2 py-1 text-sm" placeholder="Nombre firma" value={s.firma_nombre || ''} onChange={(e) => updateSocio(idx, 'firma_nombre', e.target.value)} />
											<input className="border rounded px-2 py-1 text-sm" placeholder="Cargo firma" value={s.firma_cargo || ''} onChange={(e) => updateSocio(idx, 'firma_cargo', e.target.value)} />
										</div>
									))}
								</div>
							</div>

							<div className="border rounded-lg p-3">
								<div className="flex items-center justify-between mb-2">
									<p className="text-sm font-semibold text-gray-700">Orden del dia</p>
									<button type="button" onClick={addOrdenDia} className="text-xs px-2 py-1 border rounded">Agregar punto</button>
								</div>
								<div className="space-y-2">
									{(datosCambios.orden_dia || []).map((item, idx) => (
										<div key={idx} className="flex items-center gap-2">
											<input type="checkbox" checked={item.activo !== false} onChange={(e) => updateOrdenDia(idx, { activo: e.target.checked })} />
											<input
												className="flex-1 border rounded px-2 py-1 text-sm"
												placeholder={`Punto ${idx + 1}`}
												value={item.texto || ''}
												onChange={(e) => updateOrdenDia(idx, { texto: e.target.value })}
											/>
											<button type="button" onClick={() => removeOrdenDia(idx)} className="text-xs px-2 py-1 border rounded text-red-600">Quitar</button>
										</div>
									))}
								</div>
							</div>
						</div>
					)}

					{step === 3 && (
						<div className="space-y-3">
							<p className="text-sm text-gray-700">Selecciona qué documentos deseas generar para este expediente.</p>
							<div className="space-y-2">
								{plantillas.map((p) => (
									<label key={p.id} className="flex items-center gap-2 text-sm">
										<input
											type="checkbox"
											checked={selectedPlantillas.includes(p.slug)}
											onChange={() => togglePlantilla(p.slug)}
										/>
										{p.nombre} ({p.slug})
									</label>
								))}
							</div>
							{resultado && (
								<div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 space-y-2">
									<p>Documentos generados correctamente.</p>
									{Array.isArray(resultado.documentos) && resultado.documentos.map((d) => (
										<div key={d.id} className="border rounded p-2 bg-white">
											<p className="font-medium text-emerald-800">{d.plantilla_nombre || d.id}</p>
											<div className="flex items-center gap-3 mt-1">
												{d.archivo_url && (
													<a href={d.archivo_url} target="_blank" rel="noreferrer" className="underline">
														Descargar DOCX
													</a>
												)}
												{d.archivo_pdf_url && (
													<a href={d.archivo_pdf_url} target="_blank" rel="noreferrer" className="underline font-semibold">
														Descargar PDF
													</a>
												)}
											</div>
										</div>
									))}
									{resultado.zip_descarga_url && (
										<a href={resultado.zip_descarga_url} target="_blank" rel="noreferrer" className="block underline font-semibold">
											Descargar ZIP
										</a>
									)}
								</div>
							)}
						</div>
					)}

					{error && <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg p-3">{error}</div>}
				</div>

				<div className="p-5 border-t border-gray-200 flex items-center justify-between">
					<button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm">Cerrar</button>
					<div className="flex items-center gap-2">
						<button
							disabled={step === 0 || loading}
							onClick={() => setStep((s) => Math.max(0, s - 1))}
							className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
						>
							Anterior
						</button>
						{step < 3 ? (
							<button
								disabled={!canContinue || loading}
								onClick={() => setStep((s) => Math.min(3, s + 1))}
								className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-40"
							>
								Siguiente
							</button>
						) : (
							<button
								disabled={!canContinue || loading}
								onClick={handleGenerar}
								className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-40"
							>
								{loading ? 'Generando...' : 'Generar documentos'}
							</button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
